import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const Container = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const SectionTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--gray-900);
`;

const AdminNav = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const AdminNavLink = styled(Link)`
  padding: 0.75rem 1.5rem;
  background-color: var(--gray-200);
  color: var(--gray-700);
  border-radius: var(--border-radius-md);
  font-weight: 500;
  text-decoration: none;
  transition: all var(--transition-fast) ease-in-out;
  
  &:hover {
    background-color: var(--gray-300);
  }
  
  &.active {
    background-color: var(--primary-color);
    color: white;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  min-width: 250px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  background-color: white;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${props => props.variant === 'danger' ? 'var(--danger-color)' : 'var(--primary-color)'};
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast) ease-in-out;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    background-color: var(--gray-400);
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  background: var(--white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--box-shadow-sm);
  overflow: hidden;
`;

const Th = styled.th`
  padding: 1rem;
  text-align: left;
  background-color: var(--gray-100);
  font-weight: 600;
  color: var(--gray-700);
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid var(--gray-200);
`;

const TYPE_COLORS = {
  '通用': 'var(--success-color)',
  '行业': 'var(--primary-color)',
  '技术': '#f59e0b'
};

const TypeBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: ${props => TYPE_COLORS[props.type] || 'var(--primary-color)'};
  color: white;
  border-radius: var(--border-radius-full);
  font-size: 0.75rem;
  font-weight: 500;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius-md);
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => 
    props.$status === 'active' ? 'var(--success-light)' : 
    props.$status === 'draft' ? '#fef3c7' : 'var(--danger-light)'};
  color: ${props => 
    props.$status === 'active' ? 'var(--success-dark)' : 
    props.$status === 'draft' ? '#92400e' : 'var(--danger-dark)'};
`;

const ActionButton = styled.button`
  padding: 0.4rem 0.75rem;
  margin-right: 0.5rem;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all var(--transition-fast) ease-in-out;
  
  &.edit {
    background-color: var(--primary-light);
    color: white;
  }
  
  &.delete {
    background-color: var(--danger-light);
    color: var(--danger-dark);
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const AgentBadge = styled.button`
  padding: 0.25rem 0.5rem;
  background-color: ${props => props.color || '#17a2b8'};
  color: white;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 0.7rem;
  cursor: pointer;
  margin-right: 0.25rem;
  
  &:hover {
    opacity: 0.8;
  }
`;

const AgentConfigPanel = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  margin-top: 0.5rem;
  border-radius: var(--border-radius-sm);
  border: 1px solid #e9ecef;
`;

const AgentConfigSection = styled.div`
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const AgentConfigTitle = styled.div`
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--gray-700);
  margin-bottom: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--gray-500);
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const PageButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--gray-300);
  background-color: ${props => props.active ? 'var(--primary-color)' : 'white'};
  color: ${props => props.active ? 'white' : 'var(--gray-700)'};
  border-radius: var(--border-radius-md);
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-dark)' : 'var(--gray-100)'};
  }
  
  &:disabled {
    background-color: var(--gray-100);
    cursor: not-allowed;
  }
`;

const Stats = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  background: var(--white);
  padding: 1.25rem 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--box-shadow-sm);
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatValue = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
`;

const StatLabel = styled.span`
  font-size: 0.875rem;
  color: var(--gray-600);
`;

// 批量选择相关样式
const BatchActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 1rem;
  background: #e7f3ff;
  border-radius: 8px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const SelectedCount = styled.span`
  font-weight: 500;
  color: var(--primary-color);
  margin-right: 0.5rem;
`;

const SelectionDropdown = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  background: white;
  font-size: 0.875rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const ConfirmModalContent = styled.div`
  background: var(--white);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  max-width: 500px;
  width: 90%;
`;

const WarningIcon = styled.div`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const ConfirmMessage = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  color: var(--gray-800);
`;

const ConfirmDetails = styled.div`
  background: var(--gray-100);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  margin-bottom: 1.5rem;
  max-height: 200px;
  overflow-y: auto;
`;

const ConfirmDetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--gray-200);
  font-size: 0.875rem;

  &:last-child {
    border-bottom: none;
  }
`;

const DeleteProgress = styled.div`
  margin-top: 1rem;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: var(--gray-200);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, var(--danger-color) 0%, #c82333 100%);
  transition: width 0.3s ease;
  width: ${props => props.percent}%;
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: var(--gray-600);
`;

const Toast = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 1.5rem;
  background: ${props => props.$type === 'success' ? 'var(--success-color)' : props.$type === 'error' ? 'var(--danger-color)' : 'var(--primary-color)'};
  color: white;
  border-radius: var(--border-radius-md);
  box-shadow: var(--box-shadow-md);
  z-index: 9999;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const SOLUTION_TYPES = ['通用', '行业', '技术'];

// 去除 HTML 标签，提取纯文本
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const text = doc.body.textContent || '';
  return text.trim();
};

const INDUSTRIES = {
  '通用': ['通信相关', '技术咨询', '基础设施', 'Ai相关', '云服务', '网络安全', '数据中心', '企业数字化'],
  '行业': ['医疗卫生', '生产制造', '农林牧渔', '建筑地产', '金融行业', '教育行业', '智慧城市', '智慧交通', '能源电力', '应急指挥', '公共安全', '园区景区', '酒店公寓', '餐饮零售', '物流仓储', '媒体文娱', '电子商务', '教育培训', '政府机构', '交通运输', '水利环保', '文化旅游', '会展活动', '体育健身'],
  '技术': ['大数据', '人工智能', '云计算', '物联网', '区块链', '5G应用', '边缘计算', '虚拟现实', '工业互联网']
};

const AdminSolutions = () => {
  const navigate = useNavigate();
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [industry, setIndustry] = useState('all');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0
  });
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [selectedSolutions, setSelectedSolutions] = useState([]);
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);
  const [batchDeleteProgress, setBatchDeleteProgress] = useState({ current: 0, total: 0, deleting: false });
  const [selectFilter, setSelectFilter] = useState('all');

  // Toast 提示
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchSolutions = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page,
        limit: 20
      };
      
      if (type !== 'all') {
        params.type = type;
      }
      
      if (industry !== 'all') {
        params.industry = industry;
      }
      
      if (search.trim()) {
        params.search = search.trim();
      }
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/solutions', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setSolutions(response.data.solutions || []);
      setPagination({
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1,
        total: response.data.total || 0
      });
    } catch (err) {
      setError('获取解决方案列表失败');
      console.error('获取解决方案失败:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSolutions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, industry]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (type || industry || search) {
        fetchSolutions(1);
      }
    }, 500);
    
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSolutions(1);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这个解决方案吗？')) {
      return;
    }
    
    try {
      await axios.delete(`/api/admin/solutions/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      showToast('解决方案删除成功');
      fetchSolutions(pagination.currentPage);
    } catch (err) {
      showToast(err.response?.data?.message || '删除解决方案失败', 'error');
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedSolutions.length === 0) {
      showToast('请先选择要删除的解决方案', 'error');
      return;
    }

    setShowBatchDeleteModal(true);
    setBatchDeleteProgress({ current: 0, total: selectedSolutions.length, deleting: false });
  };

  const confirmBatchDelete = async () => {
    if (selectedSolutions.length === 0) return;

    setBatchDeleteProgress({ current: 0, total: selectedSolutions.length, deleting: true });

    try {
      // 分批删除，每批10条
      const batchSize = 10;
      let deletedCount = 0;
      const totalBatches = Math.ceil(selectedSolutions.length / batchSize);

      for (let i = 0; i < totalBatches; i++) {
        const batch = selectedSolutions.slice(i * batchSize, (i + 1) * batchSize);
        
        for (const id of batch) {
          await axios.delete(`/api/admin/solutions/${id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
          });
          deletedCount++;
          setBatchDeleteProgress({ 
            current: deletedCount, 
            total: selectedSolutions.length, 
            deleting: true 
          });
        }
      }

      setShowBatchDeleteModal(false);
      showToast(`成功删除 ${deletedCount} 个解决方案`);
      setSelectedSolutions([]);
      setSelectFilter('all');
      fetchSolutions(1);
    } catch (err) {
      console.error('批量删除失败:', err);
      showToast(err.response?.data?.message || '批量删除失败', 'error');
      setShowBatchDeleteModal(false);
    }
  };

  // 全选/取消全选
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedSolutions(solutions.map(s => s._id));
    } else {
      setSelectedSolutions([]);
    }
  };

  // 选择单个
  const handleSelect = (id) => {
    if (selectedSolutions.includes(id)) {
      setSelectedSolutions(selectedSolutions.filter(i => i !== id));
    } else {
      setSelectedSolutions([...selectedSolutions, id]);
    }
  };

  // 按类型筛选选择
  const handleSelectByType = (t) => {
    if (t === 'all') {
      setSelectedSolutions(solutions.map(s => s._id));
    } else {
      setSelectedSolutions(solutions.filter(s => s.type === t).map(s => s._id));
    }
    setSelectFilter(t);
  };

  // 快捷键支持
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + A: 全选当前页
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        e.preventDefault();
        setSelectedSolutions(solutions.map(s => s._id));
      }
      // Delete: 批量删除选中项
      if (e.key === 'Delete' && selectedSolutions.length > 0 && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        e.preventDefault();
        handleBatchDelete();
      }
      // Escape: 取消选择
      if (e.key === 'Escape' && selectedSolutions.length > 0) {
        setSelectedSolutions([]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solutions, selectedSolutions]);

  const generalCount = solutions.filter(s => s.type === '通用').length;
  const industryCount = solutions.filter(s => s.type === '行业').length;
  const techCount = solutions.filter(s => s.type === '技术').length;
  const allSelected = solutions.length > 0 && selectedSolutions.length === solutions.length;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchSolutions(newPage);
    }
  };

  const handleReset = () => {
    setSearch('');
    setType('all');
    setIndustry('all');
    fetchSolutions(1);
  };

  return (
    <AdminLayout title="解决方案管理">
      <Container>
        <SectionTitle>解决方案管理</SectionTitle>
        <AdminNav>
          <AdminNavLink to="/admin/solutions" className="active">解决方案列表</AdminNavLink>
          <AdminNavLink to="/admin/solutions/new">添加解决方案</AdminNavLink>
        </AdminNav>

        <Stats>
          <StatCard>
            <StatValue>{pagination.total}</StatValue>
            <StatLabel>全部解决方案</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{generalCount}</StatValue>
            <StatLabel>通用解决方案</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{industryCount}</StatValue>
            <StatLabel>行业解决方案</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{techCount}</StatValue>
            <StatLabel>技术解决方案</StatLabel>
          </StatCard>
        </Stats>
        
        <FilterBar>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.5rem' }}>
            <SearchInput
              type="text"
              placeholder="搜索解决方案标题或描述..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button type="submit">搜索</Button>
          </form>
          
          <Select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setIndustry('all');
            }}
          >
            <option value="all">全部类型</option>
            {SOLUTION_TYPES.map(t => (
              <option key={t} value={t}>{t}解决方案</option>
            ))}
          </Select>
          
          <Select
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            disabled={type === 'all'}
          >
            <option value="all">全部行业</option>
            {(type === 'all' ? Object.values(INDUSTRIES).flat() : INDUSTRIES[type] || []).map(ind => (
              <option key={ind} value={ind}>{ind}</option>
            ))}
          </Select>
          
          <Button onClick={handleReset}>
            重置筛选
          </Button>
        </FilterBar>
        
        {error && (
          <div style={{ color: 'var(--danger-color)', marginBottom: '1rem' }}>{error}</div>
        )}
        
        {/* 批量操作栏 */}
        {selectedSolutions.length > 0 && (
          <BatchActions>
            <SelectedCount>已选择 {selectedSolutions.length} 项</SelectedCount>
            
            {/* 按类型快速选择 */}
            <span style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>快速选择:</span>
            <SelectionDropdown value={selectFilter} onChange={(e) => handleSelectByType(e.target.value)}>
              <option value="all">全部 {solutions.length} 条</option>
              <option value="通用">通用解决方案 ({solutions.filter(s => s.type === '通用').length})</option>
              <option value="行业">行业解决方案 ({solutions.filter(s => s.type === '行业').length})</option>
              <option value="技术">技术解决方案 ({solutions.filter(s => s.type === '技术').length})</option>
            </SelectionDropdown>
            
            <Button variant="danger" onClick={handleBatchDelete}>🗑️ 批量删除</Button>
            <Button onClick={() => { setSelectedSolutions([]); setSelectFilter('all'); }}>取消选择</Button>
            
            {/* 快捷键提示 */}
            <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginLeft: '0.5rem' }}>
              快捷键: Ctrl+A 全选 | Delete 删除 | Esc 取消
            </span>
          </BatchActions>
        )}
        
        {loading ? (
          <EmptyState>加载中...</EmptyState>
        ) : solutions.length === 0 ? (
          <EmptyState>
            <p>暂无解决方案数据</p>
            <Button onClick={() => navigate('/admin/solutions/new')} style={{ marginTop: '1rem' }}>
              添加第一个解决方案
            </Button>
          </EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th style={{ width: '40px' }}>
                    <Checkbox
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </Th>
                  <Th>解决方案标题</Th>
                  <Th>类型</Th>
                  <Th>行业分类</Th>
                  <Th>状态</Th>
                  <Th>标签</Th>
                  <Th>智能体配置</Th>
                  <Th>操作</Th>
                </tr>
              </thead>
              <tbody>
                {solutions.map((solution) => (
                  <React.Fragment key={solution._id}>
                    <tr style={{ background: selectedSolutions.includes(solution._id) ? '#e7f3ff' : 'transparent' }}>
                      <Td>
                        <Checkbox
                          type="checkbox"
                          checked={selectedSolutions.includes(solution._id)}
                          onChange={() => handleSelect(solution._id)}
                        />
                      </Td>
                      <Td>
                        <div style={{ fontWeight: 500 }}>{solution.title}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                          {stripHtml(solution.description)?.substring(0, 60) || '暂无描述'}
                        </div>
                      </Td>
                      <Td>
                        <TypeBadge type={solution.type}>{solution.type}</TypeBadge>
                      </Td>
                      <Td>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: 'var(--gray-200)',
                          borderRadius: 'var(--border-radius-sm)',
                          fontSize: '0.75rem'
                        }}>
                          {solution.industry}
                        </span>
                      </Td>
                      <Td>
                        <StatusBadge $status={solution.status || 'active'}>
                          {solution.status === 'active' ? '已发布' : solution.status === 'draft' ? '草稿' : '已下架'}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {solution.tags?.slice(0, 3).map((tag, index) => (
                            <span key={index} style={{
                              padding: '0.125rem 0.5rem',
                              backgroundColor: 'var(--gray-200)',
                              borderRadius: 'var(--border-radius-sm)',
                              fontSize: '0.75rem'
                            }}>
                              {tag}
                            </span>
                          ))}
                          {solution.tags?.length > 3 && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                              +{solution.tags.length - 3}
                            </span>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          <AgentBadge color="#17a2b8" onClick={() => setExpandedAgent(expandedAgent === solution._id ? null : solution._id)}>
                            关键词 {solution.keywords?.length || 0}
                          </AgentBadge>
                          <AgentBadge color="#667eea" onClick={() => setExpandedAgent(expandedAgent === solution._id ? null : solution._id)}>
                            技能 {solution.skills?.length || 0}
                          </AgentBadge>
                          <AgentBadge color="#ffc107" onClick={() => setExpandedAgent(expandedAgent === solution._id ? null : solution._id)}>
                            限制 {solution.constraints?.length || 0}
                          </AgentBadge>
                        </div>
                      </Td>
                      <Td>
                        <ActionButton
                          className="edit"
                          onClick={() => navigate(`/admin/solutions/edit/${solution._id}`)}
                        >
                          编辑
                        </ActionButton>
                        <ActionButton
                          className="delete"
                          onClick={() => handleDelete(solution._id)}
                        >
                          删除
                        </ActionButton>
                      </Td>
                    </tr>
                    {expandedAgent === solution._id && (
                      <tr>
                        <Td colSpan="7" style={{ paddingTop: 0 }}>
                          <AgentConfigPanel>
                            <AgentConfigSection>
                              <AgentConfigTitle>🔑 关键词 ({solution.keywords?.length || 0})</AgentConfigTitle>
                              {solution.keywords?.length > 0 ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                  {solution.keywords.map((kw, i) => (
                                    <span key={i} style={{
                                      padding: '0.25rem 0.5rem',
                                      background: '#17a2b8',
                                      color: 'white',
                                      borderRadius: '4px',
                                      fontSize: '0.8rem'
                                    }}>
                                      {kw}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ color: '#6c757d', fontSize: '0.85rem' }}>暂无配置关键词</span>
                              )}
                            </AgentConfigSection>
                            <AgentConfigSection>
                              <AgentConfigTitle>🛠️ 技能 ({solution.skills?.length || 0})</AgentConfigTitle>
                              {solution.skills?.length > 0 ? (
                                <div>
                                  {solution.skills.map((skill, i) => (
                                    <div key={i} style={{
                                      display: 'inline-block',
                                      padding: '0.25rem 0.5rem',
                                      background: '#667eea',
                                      color: 'white',
                                      borderRadius: '4px',
                                      fontSize: '0.8rem',
                                      marginRight: '0.25rem',
                                      marginBottom: '0.25rem'
                                    }}>
                                      {skill.name}
                                      {skill.description && <span style={{ opacity: 0.8 }}> - {skill.description}</span>}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ color: '#6c757d', fontSize: '0.85rem' }}>暂无配置技能</span>
                              )}
                            </AgentConfigSection>
                            <AgentConfigSection>
                              <AgentConfigTitle>⚠️ 限制条件 ({solution.constraints?.length || 0})</AgentConfigTitle>
                              {solution.constraints?.length > 0 ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                  {solution.constraints.map((c, i) => (
                                    <span key={i} style={{
                                      padding: '0.25rem 0.5rem',
                                      background: '#ffc107',
                                      color: '#212529',
                                      borderRadius: '4px',
                                      fontSize: '0.8rem'
                                    }}>
                                      {c}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ color: '#6c757d', fontSize: '0.85rem' }}>暂无配置限制条件</span>
                              )}
                            </AgentConfigSection>
                          </AgentConfigPanel>
                        </Td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
            
            {pagination.totalPages > 1 && (
              <Pagination>
                <PageButton
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.currentPage === 1}
                >
                  首页
                </PageButton>
                <PageButton
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                >
                  上一页
                </PageButton>
                <PageButton disabled>
                  第 {pagination.currentPage} / {pagination.totalPages} 页
                </PageButton>
                <PageButton
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  下一页
                </PageButton>
                <PageButton
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  末页
                </PageButton>
              </Pagination>
            )}
          </>
        )}

        {/* Toast 提示 */}
        {toast && <Toast $type={toast.type}>{toast.message}</Toast>}

        {/* 批量删除确认模态框 */}
        {showBatchDeleteModal && (
          <Modal onClick={() => !batchDeleteProgress.deleting && setShowBatchDeleteModal(false)}>
            <ConfirmModalContent onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: 0, color: 'var(--gray-900)' }}>确认删除</h3>
                {!batchDeleteProgress.deleting && (
                  <button
                    onClick={() => setShowBatchDeleteModal(false)}
                    style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--gray-500)' }}
                  >
                    &times;
                  </button>
                )}
              </div>
              
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <WarningIcon>⚠️</WarningIcon>
                
                {!batchDeleteProgress.deleting ? (
                  <>
                    <ConfirmMessage>
                      确定要删除选中的 <strong>{selectedSolutions.length}</strong> 个解决方案吗？
                    </ConfirmMessage>
                    <p style={{ textAlign: 'center', color: 'var(--danger-color)', marginBottom: '1rem' }}>
                      此操作不可撤销，删除后数据将无法恢复！
                    </p>
                    
                    <ConfirmDetails>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--gray-800)' }}>
                        即将删除的解决方案列表：
                      </div>
                      {solutions
                        .filter(s => selectedSolutions.includes(s._id))
                        .slice(0, 10)
                        .map(s => (
                          <ConfirmDetailItem key={s._id}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {s.title}
                            </span>
                            <TypeBadge type={s.type} style={{ marginLeft: '0.5rem', flexShrink: 0 }}>
                              {s.type}
                            </TypeBadge>
                          </ConfirmDetailItem>
                        ))}
                      {selectedSolutions.length > 10 && (
                        <div style={{ textAlign: 'center', color: 'var(--gray-500)', padding: '0.5rem', fontSize: '0.85rem' }}>
                          ...还有 {selectedSolutions.length - 10} 条未显示
                        </div>
                      )}
                    </ConfirmDetails>
                    
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                      <Button onClick={() => setShowBatchDeleteModal(false)}>取消</Button>
                      <Button variant="danger" onClick={confirmBatchDelete}>确认删除</Button>
                    </div>
                  </>
                ) : (
                  <DeleteProgress>
                    <ConfirmMessage>正在删除...</ConfirmMessage>
                    <ProgressBar>
                      <ProgressFill percent={Math.round((batchDeleteProgress.current / batchDeleteProgress.total) * 100)} />
                    </ProgressBar>
                    <ProgressText>
                      {batchDeleteProgress.current} / {batchDeleteProgress.total} 个已删除
                    </ProgressText>
                  </DeleteProgress>
                )}
              </div>
            </ConfirmModalContent>
          </Modal>
        )}
      </Container>
    </AdminLayout>
  );
};

export default AdminSolutions;